import {
  useState,
  useEffect,
  useRef,
  createContext,
  useContext,
  useMemo,
  useCallback,
} from "react";
import useAppLanguage from "../hooks/useAppLanguage";
import { translateSurvey } from "../utils/lang";
import LanguageContext from "./language";
import UserContext from "./user";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import sortOnField from "../utils/sortOnField";
import { getAnswerValue, parseSubmission } from "../utils/partial";

const SCHOOL_QUESTION_MAPPING = {
  0: "ss1g",
  1: "ss2g",
  2: "uni",
  3: "m2l",
  4: "dtt",
};

const BorseContext = createContext({
  campaign: undefined,
  survey: undefined,
  requests: undefined,
  error: undefined,
  navigation: undefined,
  labels: undefined,
  status: undefined,
  appLang: undefined,
  currentSectionIndex: undefined,
  languages: undefined,
  path: undefined,
  formData: undefined,
  partial: undefined,
  currentRequest: undefined,
  isFetching: undefined,
  isRequestFetching: undefined,
  handlePrevious: () => 0,
  handleNext: () => 0,
  setAppLang: () => 0,
  setNavigation: () => 0,
  setStatus: () => 0,
  setCurrentSectionIndex: () => 0,
  setPath: () => 0,
  setFormData: () => 0,
  setCurrentRequest: () => 0,
  setRequests: () => 0,
});

export const BorseProvider = ({ children }) => {
  const { callApi, logged, userProfile } = useContext(UserContext);
  const { language, defaultLabels, unloggedLanguage } = useContext(LanguageContext);
  const [navigation, setNavigation] = useState();
  const [status, setStatus] = useState(); //initiative-closed, initiative-open
  const [error, setError] = useState();
  const [currentSectionIndex, setCurrentSectionIndex] = useState();
  const locationRef = useRef();
  const [path, setPath] = useState([]);
  const [partial, setPartial] = useState();
  const [formData, setFormData] = useState();
  const [currentRequest, setCurrentRequest] = useState();
  const [requests, setRequests] = useState();
  const [schoolType, setSchoolType] = useState(); //ss1g, ss2g, uni, m2l, dtt

  const { data: campaign, isFetching } = useQuery(
    ["campaign", JSON.stringify(userProfile)],
    () => callApi("/scholarship/campaign/latest"),
    {
      enabled: !!userProfile && !navigation,
      onError: (error) => {
        setError(error);
        setStatus("closed");
      },
      onSuccess: (data) => {
        // TamTamy campaign statuses
        // DRAFT,
        // READY,
        // APPLICATION_IN_PROGRESS,
        // WAITING_FOR_EVALUATION,
        // EVALUATION_IN_PROGRESS,
        // EVALUATION_ENDED,
        // ENDED;
        if (
          data?.applicationStartDate &&
          data?.applicationEndDate &&
          data?.evaluationStartDate &&
          data?.evaluationEndDate &&
          data?.status
        ) {
          const today = dayjs();
          // const recap = {
          //   applicationStartDate: dayjs(Number(data.applicationStartDate)).format("DD/MM/YY hh:mm"),
          //   applicationEndDate: dayjs(Number(data.applicationEndDate)).format("DD/MM/YY hh:mm"),
          //   evaluationStartDate: dayjs(Number(data.evaluationStartDate)).format("DD/MM/YY hh:mm"),
          //   evaluationEndDate: dayjs(Number(data.evaluationEndDate)).format("DD/MM/YY hh:mm"),
          //   status: data?.status,
          // };
          // console.log("Latest campaign");
          // console.table(recap);
          if (
            today.isAfter(dayjs(Number(data.applicationStartDate)), "minute") &&
            today.isBefore(dayjs(Number(data.applicationEndDate)), "minute") &&
            data.status === "APPLICATION_IN_PROGRESS"
          ) {
            setStatus("request-phase");
          } else if (
            today.isAfter(dayjs(Number(data.applicationEndDate)), "minute") &&
            today.isBefore(dayjs(Number(data.evaluationStartDate)), "minute") &&
            ["WAITING_FOR_EVALUATION"].includes(data.status)
          ) {
            setStatus("request-closed-phase");
          } else if (
            today.isAfter(dayjs(Number(data.evaluationStartDate)), "minute") &&
            today.isBefore(dayjs(Number(data.evaluationEndDate)), "minute") &&
            ["EVALUATION_IN_PROGRESS"].includes(data.status)
          ) {
            setStatus("evaluation-phase");
          } else if (["EVALUATION_ENDED"].includes(data.status)) {
            setStatus("evaluation-ended");
          } else {
            setStatus("closed");
          }
        } else {
          setStatus("invalid-campaign");
        }
      },
    }
  );

  const { data: survey } = useQuery(
    ["borse-survey", campaign?.id, JSON.stringify(userProfile)],
    () => callApi(`/scholarship/campaign/${campaign.id}/survey`),
    {
      enabled: !!userProfile && !navigation && !!campaign?.id,
      select: ({ definition, ...data }) => {
        return {
          ...data,
          definition: {
            ...definition,
            sections: definition.sections.sort((a, b) =>
              a.position > b.position ? 1 : a.position < b.position ? -1 : 0
            ),
          },
        };
      },
      onError: (error) => setError(error),
    }
  );

  const { isFetching: isRequestFetching } = useQuery(
    ["requests", JSON.stringify(userProfile)],
    () => callApi("/scholarship/request/search?offset=0&limit=100&current=true"),
    {
      enabled:
        logged && !navigation && !["closed", "invalid-campaign"].includes(status) && !!survey,
      select: (data) =>
        data?.list
          ?.sort((a, b) => sortOnField(a, b, (data) => data.creationDate, true))
          ?.map((request) => ({
            ...request,
            ...(request.status === "DRAFT" && status !== "request-phase"
              ? { status: "EXPIRED_DRAFT", lastCompletedStep: 2 }
              : { status: request.status, lastCompletedStep: request.lastCompletedStep }),
          })),
      onError: (error) => setError(error),
      onSuccess: (data) => {
        setRequests(data);
      },
    }
  );

  const { appLang, setAppLang, languages, labels } = useAppLanguage(
    defaultLabels,
    !!survey && language,
    survey,
    logged && (error ? ![403, 404].includes(error.status) : true) && status !== "invalid-campaign"
      ? null
      : unloggedLanguage
  );

  const translatedSurvey = useMemo(() => {
    if (survey) {
      return translateSurvey(survey, appLang);
    }
  }, [survey, appLang]);

  const handlePrevious = useCallback(() => {
    setPath((current) => current.slice(0, current.length - 1));
    setCurrentSectionIndex((current) => current - 1);
  }, []);

  const handleNext = useCallback(
    (submissionId, nextIsGoToSection) => {
      if (submissionId) {
        setPartial(submissionId);
      }
      setPath((current) =>
        current.concat(
          nextIsGoToSection ?? translatedSurvey?.definition?.sections[currentSectionIndex + 1]?.id
        )
      );
      setCurrentSectionIndex((current) => current + 1);
    },
    [currentSectionIndex, translatedSurvey]
  );

  useEffect(() => {
    const currentLocation = navigation + "-" + currentSectionIndex + JSON.stringify(currentRequest);
    if (locationRef.current !== currentLocation) {
      locationRef.current = currentLocation;
      window.scrollTo(0, 0);
    }
  }, [navigation, currentSectionIndex, currentRequest]);

  const getPathFromRequest = useCallback(
    (request) => {
      return request?.lastCompletedStep < 0
        ? [survey?.definition?.sections?.[0]?.id]
        : request?.lastCompletedStep === 0
        ? [survey?.definition?.sections?.[0]?.id, survey?.definition?.sections?.[1]?.id]
        : request?.lastCompletedStep === 1
        ? [
            survey?.definition?.sections?.[0]?.id,
            survey?.definition?.sections?.[1]?.id,
            survey?.definition?.sections?.[1]?.questions?.[5]?.answers?.find(
              (ans) =>
                ans.id ===
                getAnswerValue(survey, request?.surveySubmission?.answers, 1, 5)?.answerId
            )?.nextSectionId,
          ]
        : [
            survey?.definition?.sections?.[0]?.id,
            survey?.definition?.sections?.[1]?.id,
            survey?.definition?.sections?.[1]?.questions?.[5]?.answers?.find(
              (ans) =>
                ans.id ===
                getAnswerValue(survey, request?.surveySubmission?.answers, 1, 5)?.answerId
            )?.nextSectionId,
            survey?.definition?.sections?.slice(-1)?.[0]?.id,
          ];
    },
    [survey]
  );

  useEffect(() => {
    if (!!currentRequest && survey) {
      if (currentRequest === "new") {
        setCurrentSectionIndex(0);
        setPath([survey.definition.sections[0].id]);
      } else {
        setFormData(parseSubmission(survey, currentRequest.surveySubmission));
        setCurrentSectionIndex(currentRequest?.lastCompletedStep + 1 ?? 0);
        setPath(getPathFromRequest(currentRequest));
      }
    } else {
      setFormData();
      setPath([]);
    }
  }, [currentRequest, survey, getPathFromRequest]);

  useEffect(() => {
    if (currentRequest && survey) {
      const schoolQuestion = survey.definition.sections[1].questions[5];
      const schoolAnswer = currentRequest?.surveySubmission?.answers?.find(
        (ans) => ans.questionId === schoolQuestion.id
      )?.answerId;
      if (schoolAnswer) {
        const index = schoolQuestion.answers?.findIndex(
          (answer) => Number(answer.id) === schoolAnswer
        );
        setSchoolType(SCHOOL_QUESTION_MAPPING[index]);
      }
    }
  }, [currentRequest, survey]);

  useEffect(() => {
    if (!logged) {
      setError(null);
    }
  }, [logged]);

  const contextValue = useMemo(
    () => ({
      campaign,
      survey: translatedSurvey,
      requests,
      error,
      navigation,
      labels,
      status,
      appLang,
      currentSectionIndex,
      languages,
      path,
      formData,
      partial,
      currentRequest,
      isFetching,
      schoolType,
      isRequestFetching,
      handlePrevious,
      handleNext,
      setAppLang,
      setNavigation,
      setCurrentSectionIndex,
      setPath,
      setFormData,
      setCurrentRequest,
      setRequests,
    }),
    [
      campaign,
      translatedSurvey,
      requests,
      error,
      navigation,
      labels,
      status,
      appLang,
      currentSectionIndex,
      languages,
      path,
      formData,
      partial,
      currentRequest,
      isFetching,
      schoolType,
      isRequestFetching,
      handlePrevious,
      handleNext,
      setAppLang,
      setNavigation,
      setCurrentSectionIndex,
      setPath,
      setFormData,
      setCurrentRequest,
      setRequests,
    ]
  );

  return <BorseContext.Provider value={contextValue}>{children}</BorseContext.Provider>;
};

export default BorseContext;
